'use client'

import { useState, useMemo } from 'react'
import { TrendingDown, EuroIcon } from 'lucide-react'

/**
 * Interactive No-Show ROI calculator.
 * Resmio shows static "1h / 0% / 0€" tiles. We let the visitor turn
 * three sliders and see the lost-revenue number drop in real time as
 * Rezaro recovers seats. Concrete, math-feel, sticky in memory.
 *
 * Math:
 *   monthlyLost = covers × avgTicket × noShowRate × workingDays / 100
 *   monthlyRecovered = monthlyLost × 0.65    // typical reminder+deposit lift
 */
type Lang = 'de' | 'en' | 'tr'

const ROI_T = {
  de: {
    eyebrow: 'No-Show-Rechner',
    headlineLead: 'Was kostet ein leerer Tisch',
    headlineAccent: 'Ihr Restaurant?',
    intro:
      'Drei Regler — Sie sehen sofort, wie viel Sie pro Monat verlieren und wie viel Rezaro durch Erinnerungen, Anzahlung und intelligente Bestätigung zurückholt.',
    coversLabel: 'Gedeckte Plätze pro Abend',
    coversSuffix: ' Gäste',
    avgTicketLabel: 'Durchschnittlicher Bon pro Gast',
    avgTicketSuffix: ' €',
    noShowLabel: 'No-Show-Quote (geschätzt)',
    noShowSuffix: ' %',
    resultEyebrow: '— Ihr Ergebnis',
    lostLabel: 'Aktueller Monatsverlust',
    recoveredLabel: 'Mit Rezaro zurückgewonnen',
    perMonth: 'pro Monat',
    yearlyLabel: 'Auf das Jahr gerechnet',
    footnote:
      'Berechnung: 65 % Rückgewinnungsquote auf Basis von Erinnerungs-SMS, Anzahlungspflicht ab gewählter Gruppengröße und intelligenter Bestätigung. Branchenwerte 2024–25.',
  },
  en: {
    eyebrow: 'No-Show Calculator',
    headlineLead: 'What does an empty table cost',
    headlineAccent: 'your restaurant?',
    intro:
      'Three sliders — see instantly how much you lose per month and how much Rezaro recovers through reminders, deposits and smart confirmation.',
    coversLabel: 'Covers seated per evening',
    coversSuffix: ' guests',
    avgTicketLabel: 'Average check per guest',
    avgTicketSuffix: ' €',
    noShowLabel: 'No-show rate (estimated)',
    noShowSuffix: ' %',
    resultEyebrow: '— Your result',
    lostLabel: 'Current monthly loss',
    recoveredLabel: 'Recovered with Rezaro',
    perMonth: 'per month',
    yearlyLabel: 'Projected over the year',
    footnote:
      'Calculation: 65% recovery rate based on reminder SMS, mandatory deposits from a chosen party size and smart confirmation. Industry figures 2024–25.',
  },
  tr: {
    eyebrow: 'No-Show Hesaplayıcı',
    headlineLead: 'Boş bir masa restoranınıza',
    headlineAccent: 'neye mal oluyor?',
    intro:
      'Üç sürgü — ayda ne kadar kaybettiğinizi ve Rezaro’nun hatırlatmalar, kapora ve akıllı onay ile ne kadarını geri kazandırdığını anında görün.',
    coversLabel: 'Akşam başına dolan koltuk',
    coversSuffix: ' misafir',
    avgTicketLabel: 'Misafir başına ortalama adisyon',
    avgTicketSuffix: ' €',
    noShowLabel: 'No-show oranı (tahmini)',
    noShowSuffix: ' %',
    resultEyebrow: '— Sonucunuz',
    lostLabel: 'Mevcut aylık kayıp',
    recoveredLabel: 'Rezaro ile geri kazanılan',
    perMonth: 'ayda',
    yearlyLabel: 'Yıllık bazda',
    footnote:
      'Hesaplama: Hatırlatma SMS’leri, seçilen grup büyüklüğünden itibaren zorunlu kapora ve akıllı onaya dayalı %65 geri kazanım oranı. 2024–25 sektör değerleri.',
  },
} as const

export function RoiCalculator({ lang = 'de' }: { lang?: Lang } = {}) {
  const t = ROI_T[lang]
  const [covers, setCovers] = useState(40)        // average dinner covers / night
  const [avgTicket, setAvgTicket] = useState(35)  // average bill in €
  const [noShowPct, setNoShowPct] = useState(10)  // current no-show rate %

  const calc = useMemo(() => {
    const workingDays = 26
    const monthlyLost = (covers * avgTicket * noShowPct * workingDays) / 100
    const monthlyRecovered = monthlyLost * 0.65
    const yearlyRecovered = monthlyRecovered * 12
    return {
      lost: Math.round(monthlyLost),
      recovered: Math.round(monthlyRecovered),
      yearly: Math.round(yearlyRecovered),
    }
  }, [covers, avgTicket, noShowPct])

  const fmt = (n: number) => n.toLocaleString('de-DE')

  return (
    <div className="rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden">
      <div className="grid lg:grid-cols-12">

        {/* Sliders — 7 cols */}
        <div className="lg:col-span-7 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-pub-rule">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="h-4 w-4 text-pub-spark" strokeWidth={1.8} />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-spark">{t.eyebrow}</p>
          </div>
          <h3 className="font-semibold leading-tight tracking-tight text-pub-ink"
            style={{ fontSize: 'clamp(1.4rem, 2.6vw, 2rem)' }}>
            {t.headlineLead}{' '}
            <span className="display-serif gradient-text">{t.headlineAccent}</span>
          </h3>
          <p className="mt-3 text-[14px] text-pub-muted leading-relaxed max-w-md">
            {t.intro}
          </p>

          <div className="mt-7 space-y-6">
            <Slider
              label={t.coversLabel}
              value={covers}
              min={10} max={250} step={5}
              suffix={t.coversSuffix}
              onChange={setCovers}
            />
            <Slider
              label={t.avgTicketLabel}
              value={avgTicket}
              min={10} max={120} step={1}
              suffix={t.avgTicketSuffix}
              onChange={setAvgTicket}
            />
            <Slider
              label={t.noShowLabel}
              value={noShowPct}
              min={1} max={25} step={1}
              suffix={t.noShowSuffix}
              onChange={setNoShowPct}
            />
          </div>
        </div>

        {/* Result — 5 cols, dark forest panel */}
        <div className="lg:col-span-5 bg-pub-dark text-white p-8 lg:p-10 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-3">
            {t.resultEyebrow}
          </p>

          <div className="space-y-7 flex-1">
            <div>
              <p className="text-[12px] text-white/55">{t.lostLabel}</p>
              <p className="mt-1 font-semibold leading-none text-white/40 line-through"
                style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                {fmt(calc.lost)} €
              </p>
            </div>
            <div>
              <p className="text-[12px] text-white/55">{t.recoveredLabel}</p>
              <p className="mt-1 font-semibold leading-none"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                  color: '#fcd9a8',
                }}>
                {fmt(calc.recovered)} €
              </p>
              <p className="mt-2 text-[11px] text-white/45">{t.perMonth}</p>
            </div>
            <div className="pt-4 border-t border-white/15">
              <p className="text-[12px] text-white/60">{t.yearlyLabel}</p>
              <p className="mt-1 text-[20px] font-semibold text-white flex items-center gap-1.5"
                style={{ fontFamily: 'var(--font-display)' }}>
                <EuroIcon className="h-4 w-4 text-pub-accent" strokeWidth={2} />
                {fmt(calc.yearly)} €
              </p>
            </div>
          </div>

          <p className="mt-7 text-[10.5px] text-white/40 leading-relaxed">
            {t.footnote}
          </p>
        </div>
      </div>
    </div>
  )
}

function Slider({ label, value, min, max, step, suffix, onChange }: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix: string
  onChange: (v: number) => void
}) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[12px] font-medium text-pub-ink">{label}</label>
        <span className="text-[15px] font-semibold text-brand-700 tabular-nums"
          style={{ fontFamily: 'var(--font-display)' }}>
          {value.toLocaleString('de-DE')}{suffix}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer slider-track"
        style={{
          background: `linear-gradient(to right, var(--color-brand-600) 0%, var(--color-brand-600) ${pct}%, var(--_pub-rule) ${pct}%, var(--_pub-rule) 100%)`,
        }}
      />
      <style>{`
        .slider-track::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          height: 18px; width: 18px; border-radius: 50%;
          background: var(--color-brand-600);
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(23,106,71,0.4);
          cursor: pointer;
        }
        .slider-track::-moz-range-thumb {
          height: 18px; width: 18px; border-radius: 50%;
          background: var(--color-brand-600);
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(23,106,71,0.4);
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}
